import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ContainerVan, ContainerVanChecklistData } from '../types/containerVan';

interface ContainerVanChecklistCategory {
  id: string;
  name: string;
  step_label: string;
  color: string;
  order: number;
  items: ContainerVanChecklistItem[];
}

interface ContainerVanChecklistItem {
  id: string;
  text: string;
  order: number;
  required: boolean;
  type?: 'text' | 'checkbox' | 'slider' | 'textarea' | 'gauge';
  icon?: string;
  value?: any;
  notes?: string;
  photos?: string[];
  unit?: string;
}

interface MergedContainerVanData {
  vanNumber: string;
  genSetNumber: string;
  location: string;
  genSetFullCapacity: string;
  genSetAverageFuelConsumption: string;
  genSetRemainingOperatingHours: string;
  lastInspectionDate: string;
  lastInspectionStatus: string;
  overallStatus: string;
  inspector: string;
  inspectionNotes: string;
}

/**
 * Find value by item ID (robust approach using actual item IDs)
 */
const findValueByItemId = (
  categories: ContainerVanChecklistCategory[] | undefined,
  itemId: string
): string => {
  try {
    if (!categories || categories.length === 0) {
      return 'N/A';
    }
    
    for (const category of categories) {
      if (category.items) {
        for (const item of category.items) {
          if (item.id === itemId) {
            let value: any = item.value;
            
            // Add unit if present
            if (item.unit) {
              value = `${value}${item.unit}`;
            }
            
            // Concatenate notes if present
            if (item.notes) {
              value = `${value} (Notes: ${item.notes})`;
            }
            
            if (value === null || value === undefined) return 'N/A';
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'number') return value.toString();
            return String(value);
          }
        }
      }
    }
    
    return 'N/A';
  } catch (error) {
    console.error('Error in findValueByItemId:', error);
    return 'N/A';
  }
};

/**
 * Get all container vans for a company from masterlist
 */
const getContainerVanMasterlist = async (companyId: string): Promise<ContainerVan[]> => {
  try {
    const containerVansRef = collection(db, 'containerVanMasterlist');
    const q = query(
      containerVansRef,
      where('companyId', '==', companyId),
      orderBy('vanNumber', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ContainerVan[];
  } catch (error) {
    console.error('Error fetching container van masterlist:', error);
    throw new Error(`Failed to fetch container van masterlist: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Get all inspection data for each van in a company (not just latest)
 */
const getAllInspections = async (companyId: string): Promise<Map<string, ContainerVanChecklistData[]>> => {
  try {
    const checklistsRef = collection(db, 'containerVanChecklist');
    const q = query(
      checklistsRef,
      where('companyId', '==', companyId),
      orderBy('vanNumber', 'asc'),
      orderBy('created_at', 'asc')
    );
    
    const querySnapshot = await getDocs(q);
    const inspections = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as ContainerVanChecklistData[];

    // Create a map with all inspections grouped by van number
    const inspectionsMap = new Map<string, ContainerVanChecklistData[]>();
    
    inspections.forEach(inspection => {
      const vanNumber = inspection.vanNumber;
      if (!inspectionsMap.has(vanNumber)) {
        inspectionsMap.set(vanNumber, []);
      }
      inspectionsMap.get(vanNumber)!.push(inspection);
    });

    return inspectionsMap;
  } catch (error) {
    console.error('Error fetching all inspections:', error);
    throw new Error(`Failed to fetch all inspections: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Format timestamp to readable date string
 */
const formatDate = (timestamp: any): string => {
  if (!timestamp) return 'N/A';
  
  let date: Date;
  if (timestamp.seconds) {
    // Firestore Timestamp
    date = new Date(timestamp.seconds * 1000);
  } else if (timestamp.toDate) {
    // Firestore Timestamp object
    date = timestamp.toDate();
  } else {
    // Regular Date
    date = new Date(timestamp);
  }
  
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Create expanded data with all inspection records for each van
 */
const createExpandedContainerVanData = (
  masterlistVans: ContainerVan[], 
  allInspections: Map<string, ContainerVanChecklistData[]>
): MergedContainerVanData[] => {
  const expandedData: MergedContainerVanData[] = [];
  
  masterlistVans.forEach(van => {
    const inspections = allInspections.get(van.vanNumber);
    
    if (inspections && inspections.length > 0) {
      // Add one record for each inspection
      inspections.forEach(inspection => {
        expandedData.push({
          vanNumber: van.vanNumber,
          genSetNumber: van.genSetNumber,
          location: van.location,
          genSetFullCapacity: van.genSetFullCapacity,
          genSetAverageFuelConsumption: van.genSetAverageFuelConsumption,
          genSetRemainingOperatingHours: inspection.genSetRemainingHours?.toString() || '',
          lastInspectionDate: formatDate(inspection.created_at),
          lastInspectionStatus: inspection.status.toUpperCase(),
          overallStatus: inspection.overallStatus.toUpperCase(),
          inspector: inspection.created_by,
          inspectionNotes: inspection.inspectionSummary || 'N/A'
        });
      });
    } else {
      // Add one record for van with no inspections
      expandedData.push({
        vanNumber: van.vanNumber,
        genSetNumber: van.genSetNumber,
        location: van.location,
        genSetFullCapacity: van.genSetFullCapacity,
        genSetAverageFuelConsumption: van.genSetAverageFuelConsumption,
        genSetRemainingOperatingHours: van.getSetRemainingOperatingHours || 'N/A',
        lastInspectionDate: 'Not Inspected',
        lastInspectionStatus: 'NOT INSPECTED',
        overallStatus: 'NOT INSPECTED',
        inspector: 'N/A',
        inspectionNotes: 'N/A'
      });
    }
  });
  
  return expandedData;
};

/**
 * Export all container vans for a company to PDF
 */
export const exportAllContainerVansToPDF = async (companyId: string, companyName: string): Promise<void> => {
  try {
    console.log(`Starting export for company: ${companyName} (${companyId})`);
    
    // Dynamic imports for better code splitting
    const [{ jsPDF }, { default: autoTable }] = await Promise.all([
      import('jspdf'),
      import('jspdf-autotable')
    ]);
    
    // Fetch data from both collections
    console.log('Fetching container van masterlist...');
    const [masterlistVans, allInspections] = await Promise.all([
      getContainerVanMasterlist(companyId),
      getAllInspections(companyId)
    ]);

    console.log(`Found ${masterlistVans.length} container vans and inspections for ${allInspections.size} vans`);

    if (masterlistVans.length === 0) {
      throw new Error('No container vans found for this company');
    }

    // Create expanded data with all inspection records
    console.log('Expanding data to include all inspection records...');
    const expandedData = createExpandedContainerVanData(masterlistVans, allInspections);

    // Create PDF
    const doc = new jsPDF('landscape', 'mm', 'a3');
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('All Container Van Report', 15, 25);
    
    if (companyName) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Company: ${companyName}`, 15, 35);
    }
    
    // Add generation date
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 15, 42);

    // Statistics summary
    const inspectedCount = expandedData.filter(record => record.lastInspectionStatus !== 'NOT INSPECTED').length;
    const notInspectedCount = expandedData.filter(record => record.lastInspectionStatus === 'NOT INSPECTED').length;
    const passCount = expandedData.filter(record => record.overallStatus === 'PASS').length;
    const warningCount = expandedData.filter(record => record.overallStatus === 'WARNING').length;
    const failCount = expandedData.filter(record => record.overallStatus === 'FAIL').length;
    const uniqueVans = new Set(expandedData.map(record => record.vanNumber)).size;

    doc.setFontSize(12);
    doc.text(`Total van in this report: ${uniqueVans} | Total Inspections: ${inspectedCount} | Not Inspected: ${notInspectedCount}`, 15, 49);
    doc.text(`Pass: ${passCount} | Warning: ${warningCount} | Fail: ${failCount}`, 15, 56);

    // Prepare table data
    const tableColumns = [
      'Date Inspected',
      'Time Inspected', 
      'Location',
      'Container Van Number',
      'Refrigerated Van Temp.',
      'GenSet Number',
      'GenSet Remaining Fuel',
      'GenSet Remaining Operating Hours',
      'GenSet Running Time',
      'Inspection Summary',
      'Overall Status',
      'Inspected By'
    ];

    const tableData = expandedData.map(record => {
      // Find the inspection for this specific record
      const inspections = allInspections.get(record.vanNumber);
      let inspection: ContainerVanChecklistData | undefined;
      
      if (inspections && inspections.length > 0) {
        // Find the inspection that matches this record's date/time
        const recordDate = record.lastInspectionDate;
        inspection = inspections.find(insp => formatDate(insp.created_at) === recordDate);
        // If not found, use the first inspection (fallback)
        if (!inspection) inspection = inspections[0];
      }
      
      const inspectionDate = record.lastInspectionDate !== 'Not Inspected' ? record.lastInspectionDate : 'N/A';
      const [dateOnly, timeOnly] = inspectionDate !== 'N/A' ? inspectionDate.split(' ') : ['N/A', 'N/A'];
      
      // Extract values from inspection categories using the findValueByItemId function
      const vanTemp = inspection ? findValueByItemId(inspection.categories, 'temperature_degrees_celsius') : 'N/A';
      const gensetFuel = inspection ? findValueByItemId(inspection.categories, 'remainin_fuel_liters') : 'N/A';
      const gensetRunningTime = inspection ? findValueByItemId(inspection.categories, 'hour_meter_reading') : 'N/A';
      
      return [
        dateOnly, // Date Inspected
        timeOnly, // Time Inspected
        record.location, // Location
        record.vanNumber, // Container Van Number
        vanTemp, // Refrigerated Van Temp
        record.genSetNumber, // GenSet Number
        gensetFuel, // GenSet Remaining Fuel
        record.genSetRemainingOperatingHours, // GenSet Remaining Operating Hours
        gensetRunningTime, // GenSet Running Time
        record.inspectionNotes, // Inspection Summary
        record.overallStatus, // Overall Status
        record.inspector // Inspected By
      ];
    });

    // Add table
    autoTable(doc, {
      head: [tableColumns],
      body: tableData,
      startY: 65,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'center'
      },
      headStyles: {
        fillColor: [56, 178, 172], // Teal color
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      didParseCell: (data) => {
        // Color coding for status column (index 10)
        if (data.column.index === 10) {
          const cellValue = data.cell.text[0];
          if (cellValue === 'PASS' || cellValue === 'COMPLETED') {
            data.cell.styles.textColor = [34, 139, 34]; // Green
            data.cell.styles.fontStyle = 'bold';
          } else if (cellValue === 'WARNING' || cellValue === 'ONGOING') {
            data.cell.styles.textColor = [255, 140, 0]; // Orange
            data.cell.styles.fontStyle = 'bold';
          } else if (cellValue === 'FAIL') {
            data.cell.styles.textColor = [220, 20, 60]; // Red
            data.cell.styles.fontStyle = 'bold';
          } else if (cellValue === 'NOT INSPECTED') {
            data.cell.styles.textColor = [128, 128, 128]; // Gray
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: 10, right: 10 },
      pageBreak: 'auto',
      tableWidth: 'auto',
    //   columnStyles: {
    //     0: { cellWidth: 22 }, // Date Inspected
    //     1: { cellWidth: 18 }, // Time Inspected
    //     2: { cellWidth: 25 }, // Location
    //     3: { cellWidth: 22 }, // Container Van Number
    //     4: { cellWidth: 20 }, // Refrigerated Van Temp
    //     5: { cellWidth: 20 }, // GenSet Number
    //     6: { cellWidth: 20 }, // GenSet Remaining Fuel
    //     7: { cellWidth: 25 }, // GenSet Remaining Operating Hours
    //     8: { cellWidth: 20 }, // GenSet Running Time
    //     9: { cellWidth: 30 }, // Inspection Summary
    //     10: { cellWidth: 20 }, // Overall Status
    //     11: { cellWidth: 25 }  // Inspected By
    //   }
    });

    // Add footer with total count
    const finalY = (doc as any).lastAutoTable.finalY || 50;
    doc.setFontSize(10);
    doc.text(`Total Records: ${tableData.length}`, 15, finalY + 15);

    // Add footer with page numbers
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(
            `Page ${i} of ${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    };

    // Save the PDF
    const fileName = `all-container-van-report_${companyName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    console.log(`Saving PDF as: ${fileName}`);
    doc.save(fileName);

    console.log('Export completed successfully');

  } catch (error) {
    console.error('Error exporting container van fleet report:', error);
    throw new Error(`Failed to export container van fleet report: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};